import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { formatDate } from "../lib/date";
import {
  Users,
  Building2,
  Network,
  Fingerprint,
  UserCheck,
  UserX,
  AlertCircle,
  AlertTriangle,
  TrendingUp,
  ArrowRight,
  UserMinus,
  Stethoscope,
  Wallet,
  X,
} from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import { Badge } from "../components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import api from "../lib/api";
import BranchAttendanceBoard from "../components/BranchAttendanceBoard";

const statConfig = [
  { key: "active_employees", label: "Empleados activos", icon: Users, color: "text-emerald-600 bg-emerald-50" },
  { key: "total_branches", label: "Sucursales", icon: Building2, color: "text-blue-600 bg-blue-50" },
  { key: "total_departments", label: "Departamentos", icon: Network, color: "text-violet-600 bg-violet-50" },
  { key: "total_devices", label: "Dispositivos", icon: Fingerprint, color: "text-amber-600 bg-amber-50" },
  { key: "today_present", label: "Presentes hoy", icon: UserCheck, color: "text-emerald-600 bg-emerald-50" },
  { key: "today_absent", label: "Ausencias hoy", icon: UserX, color: "text-red-600 bg-red-50" },
  { key: "today_incomplete", label: "Marcas incompletas", icon: AlertCircle, color: "text-amber-600 bg-amber-50" },
  { key: "total_employees", label: "Total empleados", icon: TrendingUp, color: "text-zinc-600 bg-zinc-100" },
];

const statByKey = Object.fromEntries(statConfig.map((s) => [s.key, s]));

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [incidentsToday, setIncidentsToday] = useState(null);
  const [payrollSnap, setPayrollSnap] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats").then((r) => setStats(r.data)),
      api.get("/hr-alerts", { params: { status: "pending", limit: 5 } }).then((r) => setAlerts(r.data)),
      api.get("/incidents/active-today").then((r) => setIncidentsToday(r.data)),
      api.get("/dashboard/payroll-snapshot").then((r) => setPayrollSnap(r.data)).catch(() => setPayrollSnap(null)),
    ])
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div data-testid="dashboard-loading">
        <Skeleton className="h-9 w-48 mb-2" />
        <Skeleton className="h-4 w-64 mb-8" />
        <div className="grid grid-cols-12 gap-4">
          <Skeleton className="col-span-12 lg:col-span-8 h-[280px] rounded-xl" />
          <Skeleton className="col-span-12 lg:col-span-4 h-[136px] rounded-xl" />
          <Skeleton className="col-span-12 lg:col-span-4 h-[136px] rounded-xl" />
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="col-span-6 sm:col-span-4 lg:col-span-2 h-[110px] rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const hasAlerts = alerts.length > 0;
  const hasIncidents = !!incidentsToday && (incidentsToday.absent_count > 0 || incidentsToday.incapacities_count > 0);

  return (
    <div data-testid="dashboard-page">
      <div className="mb-8">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950">
          Dashboard
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Resumen general del sistema VETCOMER
        </p>
      </div>

      <BranchAttendanceBoard />

      <div className="grid grid-cols-12 gap-4 auto-rows-min">
        {/* ── HERO ROW ──────────────────────────────────────────────── */}

        {/* Payroll snapshot (hero card) */}
        {payrollSnap?.has_period ? (
          <div className="col-span-12 lg:col-span-8 lg:row-span-2">
            <PayrollSnapshotWidget data={payrollSnap} />
          </div>
        ) : (
          <Card className="col-span-12 lg:col-span-8 lg:row-span-2 bg-white border-zinc-200 flex items-center justify-center min-h-[260px]" data-testid="dashboard-no-payroll">
            <CardContent className="text-center">
              <Wallet className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
              <p className="text-sm text-zinc-500">No hay periodo de planilla activo.</p>
              <Link to="/nomina" className="text-xs font-medium text-zinc-700 hover:text-zinc-950 mt-2 inline-flex items-center gap-1">
                Ir a Nómina <ArrowRight className="w-3 h-3" />
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Empleados activos (big number, accent card) */}
        <HeroNumberCard
          label="Empleados activos"
          value={stats?.active_employees ?? 0}
          icon={Users}
          variant="emerald"
          to="/empleados"
          testId="hero-active-employees"
        />

        {/* Total empleados */}
        <HeroNumberCard
          label="Total empleados"
          value={stats?.total_employees ?? 0}
          icon={TrendingUp}
          variant="zinc"
          subtitle={stats?.total_employees && stats?.active_employees
            ? `${stats.total_employees - stats.active_employees} inactivos`
            : ""}
          testId="hero-total-employees"
        />

        {/* ── TODAY'S PULSE ─────────────────────────────────────────── */}

        <SectionLabel text="Pulso de hoy" />

        <StatTile
          label="Presentes"
          value={stats?.today_present ?? 0}
          icon={UserCheck}
          accent="emerald"
          testId="tile-today-present"
        />
        <StatTile
          label="Ausencias"
          value={stats?.today_absent ?? 0}
          icon={UserX}
          accent="red"
          testId="tile-today-absent"
        />
        <StatTile
          label="Marcas incompletas"
          value={stats?.today_incomplete ?? 0}
          icon={AlertCircle}
          accent="amber"
          testId="tile-today-incomplete"
        />
        <StatTile
          label="Sucursales"
          value={stats?.total_branches ?? 0}
          icon={Building2}
          accent="blue"
          testId="tile-total-branches"
        />
        <StatTile
          label="Departamentos"
          value={stats?.total_departments ?? 0}
          icon={Network}
          accent="violet"
          testId="tile-total-departments"
        />
        <StatTile
          label="Dispositivos"
          value={stats?.total_devices ?? 0}
          icon={Fingerprint}
          accent="amber"
          testId="tile-total-devices"
        />

        {/* ── ALERTAS / INCIDENCIAS ─────────────────────────────────── */}

        {hasAlerts && (
          <Card className="col-span-12 lg:col-span-8 bg-amber-50/50 border-amber-200" data-testid="dashboard-alerts-widget">
            <CardContent className="p-0">
              <div className="px-5 py-3 border-b border-amber-200/60 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-900">
                    Alertas para RRHH
                  </h3>
                  <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold">
                    {alerts.length}
                  </span>
                </div>
                <Link
                  to="/asistencia"
                  className="text-xs font-medium text-amber-800 hover:text-amber-950 flex items-center gap-1 transition-colors"
                  data-testid="dashboard-alerts-link"
                >
                  Revisar en Asistencia
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="divide-y divide-amber-200/50">
                {alerts.slice(0, 4).map((a) => (
                  <div key={a.id} className="px-5 py-2.5">
                    <p className="text-xs font-medium text-amber-950">
                      {a.title} · <span className="font-normal">{a.employee_name}</span>
                      {a.employee_code && (
                        <span className="text-amber-700 font-mono ml-1">{a.employee_code}</span>
                      )}
                      <span className="text-amber-700 font-mono ml-2">{a.date}</span>
                    </p>
                  </div>
                ))}
                {alerts.length > 4 && (
                  <div className="px-5 py-2 bg-amber-100/30">
                    <p className="text-[11px] text-amber-800">
                      +{alerts.length - 4} alerta{alerts.length - 4 !== 1 ? "s" : ""} más
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {hasIncidents && (
          <>
            <IncidentBentoCard
              colSpan={hasAlerts ? "lg:col-span-4" : "lg:col-span-6"}
              icon={UserMinus}
              accent="red"
              title="Empleados ausentes hoy"
              count={incidentsToday.absent_count}
              items={incidentsToday.absent_today}
              link="/incidencias"
              testId="dashboard-absent-link"
            />
            {!hasAlerts && (
              <IncidentBentoCard
                colSpan="lg:col-span-6"
                icon={Stethoscope}
                accent="blue"
                title="Incapacidades activas"
                count={incidentsToday.incapacities_count}
                items={incidentsToday.active_incapacities}
                link="/incidencias"
                testId="dashboard-incapacities-link"
                showFolio
              />
            )}
          </>
        )}

        {hasIncidents && hasAlerts && (
          <IncidentBentoCard
            colSpan="lg:col-span-12"
            icon={Stethoscope}
            accent="blue"
            title="Incapacidades activas"
            count={incidentsToday.incapacities_count}
            items={incidentsToday.active_incapacities}
            link="/incidencias"
            testId="dashboard-incapacities-link"
            showFolio
          />
        )}

        {/* ── BOTTOM ROW ────────────────────────────────────────────── */}

        <Card className="col-span-12 lg:col-span-7 bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide">
                Empleados por sucursal
              </h3>
              <Building2 className="w-4 h-4 text-zinc-300" />
            </div>
            {stats?.employees_by_branch?.length > 0 ? (
              <div className="space-y-3">
                {stats.employees_by_branch.map((b, i) => {
                  const maxCount = Math.max(...stats.employees_by_branch.map((x) => x.count), 1);
                  const pct = (b.count / maxCount) * 100;
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-sm text-zinc-700 flex-1 truncate">{b.branch_name}</span>
                      <div className="flex-[2] h-1.5 rounded-full bg-zinc-100 overflow-hidden max-w-[240px]">
                        <div className="h-full bg-zinc-900 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-sm font-bold text-zinc-900 font-mono w-10 text-right">{b.count}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 text-center py-6">
                No hay sucursales registradas
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="col-span-12 lg:col-span-5 bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide">
                Empleados recientes
              </h3>
              <Link to="/empleados" className="text-[11px] font-medium text-zinc-500 hover:text-zinc-900 flex items-center gap-1">
                Ver todos <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            {stats?.recent_employees?.length > 0 ? (
              <div className="space-y-3">
                {stats.recent_employees.map((e, i) => (
                  <Link
                    key={i}
                    to={`/empleados/${e.id}`}
                    className="flex items-center justify-between py-2 border-b border-zinc-50 last:border-0 hover:bg-zinc-50/50 -mx-2 px-2 rounded transition-colors"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-zinc-900 font-medium truncate">
                        {e.full_name}
                      </p>
                      <p className="text-xs text-zinc-400 font-mono">
                        {e.employee_code}
                        {e.position && ` · ${e.position}`}
                      </p>
                    </div>
                    <span className="text-xs text-zinc-400 shrink-0 ml-2">{formatDate(e.hire_date)}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-400 text-center py-6">
                No hay empleados registrados
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


function money(n) {
  return new Intl.NumberFormat("es-CR", {
    style: "currency",
    currency: "CRC",
    maximumFractionDigits: 0,
  }).format(n || 0);
}

// ── Bento helper components ─────────────────────────────────────────

const heroVariants = {
  emerald: {
    bg: "bg-gradient-to-br from-emerald-50 via-white to-white",
    border: "border-emerald-100",
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-700",
    value: "text-emerald-950",
    accent: "bg-emerald-500",
  },
  zinc: {
    bg: "bg-zinc-950",
    border: "border-zinc-900",
    iconBg: "bg-white/10",
    iconColor: "text-white",
    value: "text-white",
    accent: "bg-white/40",
    isDark: true,
  },
};

function HeroNumberCard({ label, value, icon: Icon, variant = "emerald", subtitle, to, testId }) {
  const v = heroVariants[variant] || heroVariants.emerald;
  const wrapClass = "col-span-12 sm:col-span-6 lg:col-span-4";
  const inner = (
    <Card
      className={`${v.bg} ${v.border} shadow-[0_1px_3px_rgba(0,0,0,0.02)] hover:shadow-md transition-all duration-200 relative overflow-hidden h-full ${to ? "cursor-pointer" : ""}`}
      data-testid={testId}
    >
      <CardContent className="p-5 relative z-10">
        <div className="flex items-start justify-between mb-3">
          <p className={`text-[10px] font-semibold uppercase tracking-[0.08em] ${v.isDark ? "text-zinc-400" : "text-zinc-500"}`}>
            {label}
          </p>
          <div className={`w-9 h-9 rounded-lg ${v.iconBg} flex items-center justify-center shrink-0`}>
            <Icon className={`w-4 h-4 ${v.iconColor}`} strokeWidth={1.75} />
          </div>
        </div>
        <p className={`text-5xl font-bold tracking-tight ${v.value} font-mono leading-none`}>
          {value}
        </p>
        {subtitle && (
          <p className={`text-[11px] mt-2 ${v.isDark ? "text-zinc-400" : "text-zinc-500"}`}>
            {subtitle}
          </p>
        )}
      </CardContent>
      {to && (
        <ArrowRight className={`w-4 h-4 absolute bottom-4 right-4 ${v.isDark ? "text-zinc-500" : "text-zinc-300"}`} />
      )}
    </Card>
  );
  return to ? (
    <Link to={to} className={`${wrapClass} block`}>
      {inner}
    </Link>
  ) : (
    <div className={wrapClass}>{inner}</div>
  );
}

const tileAccents = {
  emerald: { bg: "bg-emerald-50", icon: "text-emerald-600", ring: "ring-emerald-200/0" },
  red:     { bg: "bg-red-50",     icon: "text-red-600",     ring: "ring-red-200/0" },
  amber:   { bg: "bg-amber-50",   icon: "text-amber-600",   ring: "ring-amber-200/0" },
  blue:    { bg: "bg-blue-50",    icon: "text-blue-600",    ring: "ring-blue-200/0" },
  violet:  { bg: "bg-violet-50",  icon: "text-violet-600",  ring: "ring-violet-200/0" },
};

function StatTile({ label, value, icon: Icon, accent = "emerald", testId }) {
  const a = tileAccents[accent] || tileAccents.emerald;
  return (
    <Card
      className="col-span-6 sm:col-span-4 lg:col-span-2 bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)] hover:border-zinc-300 transition-all duration-200"
      data-testid={testId}
    >
      <CardContent className="p-4">
        <div className={`w-8 h-8 rounded-lg ${a.bg} flex items-center justify-center mb-2`}>
          <Icon className={`w-4 h-4 ${a.icon}`} strokeWidth={1.75} />
        </div>
        <p className="text-2xl font-bold text-zinc-950 leading-none font-mono">{value}</p>
        <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-zinc-400 mt-1.5">
          {label}
        </p>
      </CardContent>
    </Card>
  );
}

function SectionLabel({ text }) {
  return (
    <div className="col-span-12 flex items-center gap-3 mt-2 mb-0">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
        {text}
      </span>
      <div className="flex-1 h-px bg-zinc-200" />
    </div>
  );
}

function IncidentBentoCard({ colSpan, icon: Icon, accent, title, count, items, link, testId, showFolio }) {
  const a = tileAccents[accent] || tileAccents.red;
  return (
    <Card className={`col-span-12 ${colSpan} bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)]`}>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg ${a.bg} flex items-center justify-center`}>
              <Icon className={`w-4 h-4 ${a.icon}`} strokeWidth={1.75} />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-400">{title}</p>
              <p className="text-2xl font-bold text-zinc-950 leading-tight font-mono">{count}</p>
            </div>
          </div>
          <Link to={link} className="text-[11px] font-medium text-zinc-500 hover:text-zinc-900 flex items-center gap-1" data-testid={testId}>
            Ver <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        {items?.length > 0 ? (
          <div className="divide-y divide-zinc-100 -mx-1">
            {items.slice(0, 4).map((i) => (
              <div key={i.id} className="py-2 px-1 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm text-zinc-800 font-medium truncate">{i.employee_name}</p>
                  <p className="text-[11px] text-zinc-400">{i.type_label}{showFolio && i.folio ? ` · ${i.folio}` : ""}</p>
                </div>
                <span className="text-[11px] font-mono text-zinc-400 shrink-0 ml-2">{i.end_date}</span>
              </div>
            ))}
            {items.length > 4 && (
              <p className="text-[11px] text-zinc-400 pt-2 px-1">+{items.length - 4} más</p>
            )}
          </div>
        ) : (
          <p className="text-xs text-zinc-400">Sin registros</p>
        )}
      </CardContent>
    </Card>
  );
}

const statusLabels = {
  draft: "Borrador",
  calculated: "Calculada",
  approved: "Aprobada",
};

const monthNames = [
  "", "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "setiembre", "octubre", "noviembre", "diciembre",
];

function PayrollSnapshotWidget({ data }) {
  const max = Math.max(1, ...data.daily_costs.map((d) => d.total));
  const halfLabel = data.half === 1 ? "1ª quincena" : "2ª quincena";
  const monthLabel = monthNames[data.month] || "";

  return (
    <Card
      className="bg-white border-zinc-200 shadow-[0_1px_3px_rgba(0,0,0,0.02)] mb-6 overflow-hidden"
      data-testid="dashboard-payroll-widget"
    >
      <CardContent className="p-0">
        {/* Header */}
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center">
              <Wallet className="w-4 h-4 text-indigo-600" strokeWidth={1.75} />
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-zinc-400 leading-tight">
                Planilla en curso
              </p>
              <p className="text-sm font-semibold text-zinc-950 leading-tight capitalize">
                {halfLabel} de {monthLabel} {data.year}
              </p>
            </div>
            <Badge
              variant="outline"
              className="ml-2 text-[10px] font-medium bg-zinc-50 border-zinc-200 text-zinc-700"
            >
              {statusLabels[data.status] || data.status}
            </Badge>
          </div>
          <Link
            to="/nomina"
            className="text-xs font-medium text-zinc-500 hover:text-zinc-900 flex items-center gap-1 transition-colors"
            data-testid="dashboard-payroll-link"
          >
            Abrir nómina
            <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-zinc-100 border-b border-zinc-100">
          <div className="px-5 py-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-400">
              Acumulado a hoy
            </p>
            <p
              className="text-xl font-bold text-zinc-950 font-mono mt-0.5"
              data-testid="payroll-snapshot-realized"
            >
              {money(data.realized_cost)}
            </p>
            <p className="text-[10px] text-zinc-400 mt-0.5">
              {data.elapsed_days} de {data.total_days} días · {data.progress_pct}%
            </p>
          </div>

          <div className="px-5 py-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-400">
              Por venir (proy.)
            </p>
            <p
              className="text-xl font-bold text-amber-700 font-mono mt-0.5"
              data-testid="payroll-snapshot-projected"
            >
              {money(data.projected_remaining)}
            </p>
            <p className="text-[10px] text-zinc-400 mt-0.5">
              {data.total_days - data.elapsed_days} días restantes
            </p>
          </div>

          <div className="px-5 py-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-400">
              Total estimado
            </p>
            <p
              className="text-xl font-bold text-indigo-700 font-mono mt-0.5"
              data-testid="payroll-snapshot-total"
            >
              {money(data.projected_total)}
            </p>
            <p className="text-[10px] text-zinc-400 mt-0.5">
              Promedio diario: {money(data.avg_daily_cost)}
            </p>
          </div>

          <div className="px-5 py-4">
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-400">
              Pago previsto
            </p>
            <p className="text-xl font-bold text-zinc-950 font-mono mt-0.5">
              {data.payment_date ? data.payment_date.slice(8, 10) + "/" + data.payment_date.slice(5, 7) : "—"}
            </p>
            <p className="text-[10px] text-zinc-400 mt-0.5">
              Corte: {data.cutoff_date ? data.cutoff_date.slice(8, 10) + "/" + data.cutoff_date.slice(5, 7) : "—"}
            </p>
          </div>
        </div>

        {/* Sparkline diario */}
        <div className="px-5 py-3">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-zinc-400">
              Costo diario del periodo
            </p>
            <div className="flex items-center gap-3 text-[10px] text-zinc-400">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm bg-indigo-500" /> Realizado
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm bg-amber-300" /> Proyectado
              </span>
            </div>
          </div>
          <div className="flex items-stretch gap-[3px] h-16" data-testid="payroll-snapshot-sparkline">
            {data.daily_costs.map((d) => {
              const heightRealized = (d.realized / max) * 100;
              const heightProjected = (d.projected / max) * 100;
              return (
                <div
                  key={d.date}
                  className={`flex-1 flex flex-col-reverse min-w-[6px] group relative ${d.is_today ? "ring-2 ring-indigo-400 rounded-sm" : ""}`}
                  title={`${d.date} · ${money(d.total)}`}
                >
                  {heightRealized > 0 && (
                    <div
                      className="bg-indigo-500 rounded-t-sm"
                      style={{ height: `${heightRealized}%` }}
                    />
                  )}
                  {heightProjected > 0 && (
                    <div
                      className="bg-amber-300 rounded-t-sm"
                      style={{ height: `${heightProjected}%` }}
                    />
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between mt-1 text-[9px] text-zinc-400 font-mono">
            <span>{data.daily_costs[0]?.date.slice(8, 10) || ""}/{data.daily_costs[0]?.date.slice(5, 7) || ""}</span>
            <span>Hoy ({data.today.slice(8, 10)}/{data.today.slice(5, 7)})</span>
            <span>{data.daily_costs.at(-1)?.date.slice(8, 10) || ""}/{data.daily_costs.at(-1)?.date.slice(5, 7) || ""}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
